import { computed, Injectable, signal } from '@angular/core';

import { LabelOption } from '@treetop/shared/model';

interface TagGridComponentState<T extends { id?: string }> {
  selectedOptions: T[];
  possibleLabelOptions: LabelOption<T>[];
}

@Injectable()
export class TagGridService<T extends { id?: string }> {
  private state = signal<TagGridComponentState<T>>({
    selectedOptions: [],
    possibleLabelOptions: [],
  });

  public selectedOptions = computed(() => this.state().selectedOptions);
  public selectedLabelOptions = computed<LabelOption<T>[]>(() => {
    const { selectedOptions, possibleLabelOptions } = this.state();

    return selectedOptions.reduce<LabelOption<T>[]>((acc, selectedOption) => {
      const labelOption = possibleLabelOptions.find((o) => o.value['id'] === selectedOption['id']);
      if (labelOption) acc.push(labelOption);
      return acc;
    }, []);
  });

  public possibleLabelOptions = computed(() => {
    const state = this.state();
    return state.possibleLabelOptions?.filter(
      (option) =>
        !state.selectedOptions.some((selectedOption) => {
          return selectedOption?.['id'] === option.value['id'];
        }),
    );
  });

  public setPossibleLabelOptions(options: LabelOption<T>[]): void {
    this.state.update((state) => ({ ...state, possibleLabelOptions: options }));
  }

  public setSelectedOptions(options: T[]): void {
    this.state.update((state) => ({ ...state, selectedOptions: options }));
  }

  /**
   * Adds the specified option to the selection if it is not already selected.
   *
   * @param optionValue - The value of the option to be added to the selection.
   *
   */
  public addSelection(optionValue: T): void {
    const option = this.state().possibleLabelOptions.find((possibleOption) => possibleOption.value['id'] === optionValue['id']);
    if (!option) return;

    this.state.update((state) => {
      if (!state.selectedOptions.map((option) => option.id).includes(option.value['id'])) {
        return {
          ...state,
          selectedOptions: [...state.selectedOptions, option.value],
        };
      }
      return state;
    });
  }

  /**
   * Removes the specified option from the selection.
   *
   * @param option - The option to be removed from the selection.
   */
  public removeSelection(option: T): void {
    this.state.update((state) => ({
      ...state,
      selectedOptions: state.selectedOptions.filter((selectedOption) => selectedOption['id'] !== option['id']),
    }));
  }

  /**
   * Reorders the selected options by moving an item from `previousIndex` to `currentIndex`.
   *
   * @param previousIndex - The index of the item to move.
   * @param currentIndex - The target index to move the item to.
   */
  public reorderSelections(previousIndex: number, currentIndex: number): void {
    this.state.update((state) => {
      const selectedOptions = [...state.selectedOptions];
      const [moved] = selectedOptions.splice(previousIndex, 1);
      selectedOptions.splice(currentIndex, 0, moved);
      return { ...state, selectedOptions };
    });
  }
}
